// SensorAlarmSummary — compact severity-count cluster shown in the cell header.
// Shows one chip per present level (only WAR/ERR light it, D3) with its count, and a
// chevron that toggles the integrated alarm list. Read-only summary (CA1, RN5, D8).
import { Stack, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import SensorLevelIcon from './SensorLevelIcon'
import { getHeaderErrors, getAggregatedLevel, countByLevel, SEVERITY_ORDER } from './sensorAlarmUtils'

function levelColor (theme, level) {
  if (level === 'WAR') return theme.palette.warning.main
  if (level === 'INF') return theme.palette.info.main
  return theme.palette.error.main
}

/**
 * Props:
 *   errors   — this cell's active sensor errors.
 *   open     — whether the integrated list is expanded.
 *   onToggle — toggles the list.
 *
 * Returns null when there are no header-level (WAR/ERR) errors (D3, V2).
 */
export default function SensorAlarmSummary ({ errors, open, onToggle }) {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()

  const headerErrors = getHeaderErrors(errors)
  if (headerErrors.length === 0) return null

  const counts = countByLevel(headerErrors)
  const aggregatedLevel = getAggregatedLevel(headerErrors)

  return (
    <Tooltip title={t_('Active alarms')}>
      <Stack
        component='button'
        type='button'
        data-testid='sensor-alarm-summary'
        data-level={aggregatedLevel}
        onClick={onToggle}
        aria-label={t_('Active alarms')}
        aria-expanded={open}
        direction='row'
        alignItems='center'
        spacing={1.25}
        sx={{
          cursor: 'pointer',
          border: `1px solid ${alpha(levelColor(theme, aggregatedLevel), 0.5)}`,
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          bgcolor: alpha(levelColor(theme, aggregatedLevel), 0.15),
          transition: theme.transitions.create(['background-color']),
          '&:hover': { bgcolor: alpha(levelColor(theme, aggregatedLevel), 0.3) },
          '&:focus-visible': { outline: `2px solid ${levelColor(theme, aggregatedLevel)}`, outlineOffset: 2 }
        }}
      >
        {SEVERITY_ORDER.filter((level) => counts[level] > 0).map((level) => (
          <Stack
            key={level}
            data-testid={`sensor-alarm-count-${level}`}
            direction='row'
            alignItems='center'
            spacing={0.5}
          >
            <SensorLevelIcon level={level} fontSize='medium' />
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold', color: levelColor(theme, level), lineHeight: 1 }}>
              {counts[level]}
            </Typography>
          </Stack>
        ))}
        {open
          ? <ExpandLessOutlined sx={{ color: 'text.secondary' }} />
          : <ExpandMoreOutlined sx={{ color: 'text.secondary' }} />}
      </Stack>
    </Tooltip>
  )
}
